import { Stack, SxProps, Typography } from '@mui/material';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { TableCell } from 'common/components/Table';
import { UserAvatar } from 'user/components/UserAvatar';
import { Chip } from 'common/components/Chip';
import { CircularProgress } from 'common/components/CircularProgress';
import { IGlobalDrawerType } from 'common/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { ITrainingUserEdgeFragment } from 'graphql/types';
import { withSxProp } from 'common/utils/props';

const userTableCellStyle: SxProps = {
    maxWidth: 300,
    cursor: 'pointer',
    borderRight: '1px solid',
    borderColor: 'divider',

    '&:focus-visible': {
        outlineOffset: '-2px',
    },
};

const scoreTableCellStyle: SxProps = {
    cursor: 'pointer',
    pl: 3,
    width: 0,

    '&:focus-visible': {
        outlineOffset: '-11px',
    },
};

interface IProps {
    participant: ITrainingUserEdgeFragment;
    canManageParticipant: boolean;
    nameCellSx?: SxProps;
    scoreCellSx?: SxProps;
}

export const TrainingParticipantSummaryCells = ({
    participant,
    canManageParticipant,
    nameCellSx,
    scoreCellSx,
}: IProps) => {
    const [translate] = useTranslation();
    const location = useLocation();
    const { openGlobalDrawer } = useGlobalDrawer();

    const {
        portfolioItemId,
        node: user,
        score,
        scoreThreshold,
        progress,
    } = participant;

    const renderParticipantTrainingScore = () => {
        if (!canManageParticipant) return null;

        const showScore = progress === 1 && score;

        if (showScore) {
            const scoreLabel = `${Math.round(score * 100)}%`;

            return (
                <Chip
                    hover
                    aria-label={`${translate('progress')} ${scoreLabel}`}
                    bgColor={
                        !scoreThreshold || score >= scoreThreshold
                            ? 'success'
                            : 'error'
                    }
                    label={scoreLabel}
                />
            );
        }

        if (!!progress) {
            const progressValue = Math.round(progress * 100);

            return (
                <CircularProgress
                    filled
                    hover
                    showTooltip
                    aria-label={`${translate('progress')} ${progressValue}%`}
                    bgColor="grey.200"
                    color="inherit"
                    size={28}
                    value={progressValue}
                    variant="determinate"
                />
            );
        }

        return (
            <Chip
                hover
                aria-label={`${translate('progress')} 0%`}
                sx={{ backgroundColor: 'grey.200' }}
            />
        );
    };

    const openParticipantDrawer = () => {
        if (!canManageParticipant) {
            openGlobalDrawer({
                type: IGlobalDrawerType.UserProfile,
                itemId: user.id,
            });

            return;
        }

        if (!portfolioItemId) return;

        openGlobalDrawer(
            {
                type: IGlobalDrawerType.PortfolioItem,
                itemId: portfolioItemId,
            },
            location
        );
    };

    return (
        <>
            <TableCell
                sx={[userTableCellStyle, ...withSxProp(nameCellSx)]}
                tabIndex={0}
                onClick={openParticipantDrawer}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;

                    openParticipantDrawer();
                }}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center' }}
                >
                    <UserAvatar size={45} user={user} />
                    <Typography
                        sx={{
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                        }}
                        variant="body2"
                    >
                        {user.name}
                    </Typography>
                </Stack>
            </TableCell>
            <TableCell
                align="center"
                sx={[scoreTableCellStyle, ...withSxProp(scoreCellSx)]}
                tabIndex={0}
                onClick={openParticipantDrawer}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;

                    openParticipantDrawer();
                }}
            >
                {renderParticipantTrainingScore()}
            </TableCell>
        </>
    );
};
